import React, { useEffect } from 'react'

import classNames from 'classnames'
import { Helmet } from 'react-helmet-async'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

import { AppLayout, PageTracker } from '@acre/design-system'
import { setVerificationCacheInvalidation, useGetCurrentUserQuery } from '@acre/graphql'

import MainNav from '@broker-crm-common/Nav/MainNav'
import useShouldOnboard from '@broker-crm-hooks/useShouldOnboard'
import { helmetData } from '@broker-crm-utils'

const AppRoutes = () => {
  const location = useLocation()
  const { caseId, documentId } = useParams<{ caseId: string; documentId: string }>()

  useEffect(() => {
    // Check if the previous URL contained 'sourcing-frontend'
    const previousUrl = document.referrer
    if (previousUrl.includes('sourcing-frontend')) {
      // If the previous URL contained sourcing-frontend, invalidate apollo verification cache
      // We need to retrieve fresh verification data as the user may have made changes in sourcing
      setVerificationCacheInvalidation()
    }
  }, [location])

  const isDocumentViewer = React.useMemo(
    () => location.pathname.endsWith(`documents/${documentId}/cases/${caseId}`),
    [location.pathname, documentId, caseId],
  )

  const isLoginPage = React.useMemo(() => location.pathname === '/login', [location.pathname])

  const { data } = useGetCurrentUserQuery()

  const user = data?.currentUser

  const shouldOnboard = useShouldOnboard(user)

  if (shouldOnboard && !location.pathname.includes('welcome')) {
    return <Navigate to="welcome" replace />
  }

  const className = classNames('router', {
    'router--navigation-spacer': !isLoginPage,
  })

  let title = window.location.pathname.split('/').pop()?.replace(/-/g, ' ') || 'unknown'
  title = title.substring(0, 1).toUpperCase() + title.substring(1, 999)

  return (
    <PageTracker helmetData={helmetData}>
      <div className={className}>
        <Helmet titleTemplate="Acre Broker CRM | %s" helmetData={helmetData}>
          <title>{title}</title>
        </Helmet>
        <AppLayout>
          {!shouldOnboard && !isDocumentViewer && <MainNav />}
          <Outlet />
        </AppLayout>
      </div>
    </PageTracker>
  )
}

export default AppRoutes
